import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Mail, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface EmailVerificationCardProps {
  email: string;
}

const EmailVerificationCard: React.FC<EmailVerificationCardProps> = ({ email }) => {
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      await supabase.auth.resend({
        type: 'signup',
        email,
      });
      setSent(true);
      toast({
        title: 'Verification email sent',
        description: 'Please check your inbox.',
      });
    } catch {
      toast({
        title: 'Unable to resend',
        description: 'Please try again in a moment.',
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <AlertCircle className="h-4 w-4 text-primary" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">Email Verification Required</p>
          <p className="text-xs text-muted-foreground">
            Please confirm your email address to activate your account.
          </p>
        </div>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={handleResend}
        disabled={resending || sent}
        className="w-full"
      >
        {resending ? (
          <>
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            Sending...
          </>
        ) : sent ? (
          <>
            <Mail className="mr-2 h-3.5 w-3.5" />
            Verification Sent
          </>
        ) : (
          <>
            <Mail className="mr-2 h-3.5 w-3.5" />
            Resend Verification Email
          </>
        )}
      </Button>

      {sent && (
        <p className="text-[11px] text-muted-foreground/70 text-center">
          Didn't receive it? Check your spam folder.
        </p>
      )}
    </div>
  );
};

export default EmailVerificationCard;
